import { EyeOffIcon } from "@/components/icons/eye-off-icon";
import { EyeOnIcon } from "@/components/icons/eye-on-icon";
import { useScrollToOnFocus } from "@/hooks/utils/use-scroll-to-on-focus";
import { cn } from "@/utils/cn";
import { textVariants } from "@/utils/text-variants";
import { InputHTMLAttributes, memo, useState } from "react";
import { Control, Controller, FieldValues, Path } from "react-hook-form";

export type TextFieldType = InputHTMLAttributes<HTMLInputElement>["type"];
export type TextFieldAutocomplete = InputHTMLAttributes<HTMLInputElement>["autoComplete"];

interface TextFieldProp<T extends FieldValues> {
  control: Control<T>;
  name: Path<T>;
  placeholder?: string;
  disabled?: boolean;
  onBlur?: () => void;
  onFocus?: () => void;
  onChange?: (value: string) => void;
  type?: TextFieldType;
  inputMode?: InputHTMLAttributes<HTMLInputElement>["inputMode"];
  autocomplete?: TextFieldAutocomplete;
  className?: string;
  id?: string;
  "aria-label"?: string;
  isError?: boolean;
  inputRef?: React.RefObject<HTMLInputElement>;
  /**
   * Enable scroll-to-focus behaviour (scrolls textfield to top 1/3 of viewport when focused)
   * Useful for preventing keyboard from covering the input on mobile
   */
  scrollToOnFocus?: boolean;
  /** Visual theme. "dark" = transparent pill for dark backgrounds; "settings" = pill with purple border for settings screens. */
  theme?: "light" | "dark" | "settings";
}

function TextFieldInner<T extends FieldValues>({
  control,
  name,
  placeholder,
  disabled = false,
  onBlur,
  onFocus,
  type,
  inputMode,
  autocomplete,
  className,
  id,
  isError = false,
  inputRef,
  scrollToOnFocus = false,
  theme = "light",
  "aria-label": ariaLabel,
}: TextFieldProp<T>) {
  const [showPassword, setShowPassword] = useState(false);
  const isPasswordField = type === "password";
  const inputType = isPasswordField ? (showPassword ? "text" : "password") : type;
  const isDark = theme === "dark";
  const isSettings = theme === "settings";

  // Built-in scroll-to-focus functionality
  const { elementRef: scrollRef, handleFocus: handleScrollFocus } =
    useScrollToOnFocus<HTMLInputElement>();

  // Use external ref if provided, otherwise use scroll ref
  const effectiveRef = inputRef || (scrollToOnFocus ? scrollRef : undefined);

  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => (
        <div className="relative w-full">
          <input
            {...field}
            ref={effectiveRef}
            id={id}
            placeholder={placeholder}
            disabled={disabled}
            type={inputType}
            inputMode={inputMode}
            autoComplete={autocomplete}
            aria-label={ariaLabel}
            value={field.value ?? ""}
            onBlur={() => {
              field.onBlur();
              onBlur?.();
            }}
            onFocus={() => {
              if (scrollToOnFocus) {
                handleScrollFocus();
              }
              onFocus?.();
            }}
            onAnimationStart={(event) => {
              // Autofill can fill the input without an `input` event; sync it (see index.css).
              if (event.animationName !== "autofill-start") return;
              const { value } = event.currentTarget;
              if (value !== field.value) field.onChange(value);
            }}
            className={cn(
              textVariants.inputText,
              "inline-block h-11.5 w-full rounded-xl border px-4 py-3 text-ellipsis focus:outline-none",
              isDark
                ? "rounded-full border-[#d6dbe7] bg-transparent text-white placeholder:text-[#7485a7] focus:border-white focus:ring-0 disabled:opacity-50"
                : "placeholder:text-text-placeholder-default focus:ring-primary border-gray-200 bg-white text-black focus:border-[var(--color-os-brand)] focus:ring-2 disabled:bg-gray-100 disabled:text-gray-400",
              isSettings &&
                "h-[52px] rounded-[27px] border-[#ebe0fb] bg-white px-[18px] shadow-[0px_8px_64.7px_0px_rgba(64,0,255,0.1)] focus:border-[#ebe0fb] focus:ring-0",
              {
                "border-border-input-error focus:ring-border-input-error placeholder:text-text-input-error-default":
                  isError && !isSettings,
                "pr-12": isPasswordField,
              },
              className,
            )}
          />
          {isPasswordField && (
            <button
              type="button"
              aria-label={showPassword ? "Hide password" : "Show password"}
              className={cn(
                "absolute inset-y-0 right-4 flex items-center focus:outline-none",
                isDark ? "text-[#7485a7] hover:text-white" : "text-gray-400 hover:text-gray-600",
              )}
              onClick={() => setShowPassword((prev) => !prev)}
            >
              {showPassword ? <EyeOnIcon /> : <EyeOffIcon />}
            </button>
          )}
        </div>
      )}
    />
  );
}

const TextField = memo(TextFieldInner) as typeof TextFieldInner;

export default TextField;
